import { DiagnosticBag } from "./diagnostics.js";
import { tokenize } from "./lexer.js";
import { err, ok } from "../result.js";
import { withDiscriminant } from "../variant.js";
class Cursor {
    tokens;
    i = 0;
    constructor(tokens) {
        this.tokens = tokens;
    }
    peek(offset = 0) {
        const t = this.tokens[this.i + offset] ?? this.tokens[this.tokens.length - 1];
        if (t === undefined) {
            throw new Error("Cursor.peek: empty token stream");
        }
        return t;
    }
    next() {
        const t = this.peek();
        if (t.kind !== "EOF") {
            this.i++;
        }
        return t;
    }
    eat(kind) {
        if (this.peek().kind === kind) {
            return this.next();
        }
        return null;
    }
    eatNewlines() {
        while (this.peek().kind === "Newline") {
            this.next();
        }
    }
}
class Parser {
    cur;
    diags;
    constructor(cur, diags) {
        this.cur = cur;
        this.diags = diags;
    }
    parseDiagram() {
        const start = this.cur.peek();
        this.cur.eatNewlines();
        // optional `typeDiagram` header
        if (this.cur.peek().kind === "TypeDiagramKw") {
            this.cur.next();
            this.cur.eatNewlines();
        }
        const decls = [];
        while (this.cur.peek().kind !== "EOF") {
            this.cur.eatNewlines();
            if (this.cur.peek().kind === "EOF") {
                break;
            }
            const decl = this.parseDeclaration();
            if (decl !== null) {
                decls.push(decl);
            }
            this.cur.eatNewlines();
        }
        const end = this.cur.peek();
        return {
            decls,
            span: spanBetween(start, end),
        };
    }
    parseDeclaration() {
        const targeting = this.parseTargetingAnnotations();
        const t = this.cur.peek();
        if (t.kind === "TypeKw") {
            return this.parseRecord(targeting);
        }
        if (t.kind === "UnionKw") {
            return this.parseUnion(false, targeting);
        }
        if (t.kind === "UntaggedKw") {
            const next = this.cur.peek(1);
            if (next.kind !== "UnionKw") {
                return this.errorAndRecover(`expected 'union' after 'untagged', got ${describe(next)}`, next);
            }
            return this.parseUnion(true, targeting);
        }
        if (t.kind === "AliasKw") {
            return this.parseAlias(targeting);
        }
        return this.errorAndRecover(`expected 'type', 'union', 'untagged union', or 'alias', got ${describe(t)}`, t);
    }
    /** Emit an error diagnostic anchored at `tok`, recover to the next decl, return null. */
    errorAndRecover(message, tok) {
        this.diags.error(message, tok.line, tok.col, tok.length || 1);
        this.recoverToTopLevel();
        return null;
    }
    parseTargetingAnnotations() {
        let targeting;
        while (this.cur.peek().kind === "At") {
            this.cur.next();
            const nameTok = this.expect("Ident", "annotation name");
            if (nameTok === null) {
                return targeting;
            }
            if (this.expect("LParen", "'('") === null) {
                return targeting;
            }
            const values = this.parseCommaSeparated("RParen", () => this.expect("Ident", "target name")?.value ?? null);
            this.expect("RParen", "')'");
            targeting ??= {};
            if (nameTok.value === "targets") {
                targeting.targets = values;
            }
            else if (nameTok.value === "skipTargets") {
                targeting.skipTargets = values;
            }
            else {
                this.diags.error(`unknown annotation '@${nameTok.value}'`, nameTok.line, nameTok.col - 1, nameTok.length + 1);
            }
            this.cur.eatNewlines();
        }
        return targeting;
    }
    /** Consume a decl keyword's name + generics, recovering on a missing name.
     * Shared opening of parseRecord/parseUnion/parseAlias. */
    parseDeclHeader(kw, nameLabel) {
        const nameTok = this.expect("Ident", nameLabel);
        if (nameTok === null) {
            this.recoverToTopLevel();
            return null;
        }
        return { kw, nameTok, generics: this.parseGenericParams() };
    }
    parseRecord(targeting) {
        const header = this.parseDeclHeader(this.cur.next(), "type name");
        if (header === null) {
            return null;
        }
        if (this.expect("LBrace", "'{'") === null) {
            this.recoverToTopLevel();
            return null;
        }
        const fields = this.parseFieldList();
        const closeTok = this.expect("RBrace", "'}'");
        return {
            kind: "record",
            name: header.nameTok.value,
            generics: header.generics,
            fields,
            ...(targeting === undefined ? {} : { targeting }),
            span: spanBetween(header.kw, closeTok ?? header.kw),
        };
    }
    parseUnion(untagged = false, targeting) {
        const start = untagged ? this.cur.next() : this.cur.peek();
        const header = this.parseDeclHeader(this.cur.next(), "union name");
        if (header === null) {
            return null;
        }
        if (this.expect("LBrace", "'{'") === null) {
            this.recoverToTopLevel();
            return null;
        }
        const variants = this.parseVariantList();
        const closeTok = this.expect("RBrace", "'}'");
        return {
            kind: "union",
            name: header.nameTok.value,
            generics: header.generics,
            ...(untagged ? { untagged: true } : {}),
            variants,
            ...(targeting === undefined ? {} : { targeting }),
            span: spanBetween(start, closeTok ?? header.kw),
        };
    }
    parseAlias(targeting) {
        const header = this.parseDeclHeader(this.cur.next(), "alias name");
        if (header === null) {
            return null;
        }
        if (this.expect("Equals", "'='") === null) {
            this.recoverToTopLevel();
            return null;
        }
        const target = this.parseTypeRef();
        if (target === null) {
            this.recoverToTopLevel();
            return null;
        }
        return {
            kind: "alias",
            name: header.nameTok.value,
            generics: header.generics,
            target,
            ...(targeting === undefined ? {} : { targeting }),
            span: spanBetween(header.kw, this.cur.peek()),
        };
    }
    parseCommaSeparated(closer, parseItem) {
        const items = [];
        while (this.cur.peek().kind !== closer && this.cur.peek().kind !== "EOF") {
            const item = parseItem();
            if (item === null) {
                break;
            }
            items.push(item);
            if (this.cur.peek().kind === "Comma") {
                this.cur.next();
            }
            else {
                break;
            }
        }
        return items;
    }
    parseGenericParams() {
        if (this.cur.peek().kind !== "LAngle") {
            return [];
        }
        this.cur.next();
        const names = this.parseCommaSeparated("RAngle", () => this.expect("Ident", "generic parameter name")?.value ?? null);
        this.expect("RAngle", "'>'");
        return names;
    }
    parseBraceList(parseItem) {
        const items = [];
        for (;;) {
            this.cur.eatNewlines();
            const t = this.cur.peek();
            if (t.kind === "RBrace" || t.kind === "EOF") {
                break;
            }
            const item = parseItem();
            if (item !== null) {
                items.push(item);
            }
            this.eatSeparator();
        }
        return items;
    }
    parseFieldList() {
        return this.parseBraceList(() => this.parseField());
    }
    parseField() {
        const nameTok = this.expect("Ident", "field name");
        if (nameTok === null) {
            this.skipToFieldBoundary();
            return null;
        }
        if (this.expect("Colon", "':'") === null) {
            this.skipToFieldBoundary();
            return null;
        }
        const type = this.parseTypeRef();
        if (type === null) {
            this.skipToFieldBoundary();
            return null;
        }
        return {
            name: nameTok.value,
            type,
            span: spanBetween(nameTok, this.cur.peek()),
        };
    }
    parseVariantList() {
        return this.parseBraceList(() => this.parseVariant());
    }
    parseVariant() {
        const nameTok = this.expect("Ident", "variant name");
        if (nameTok === null) {
            this.skipToFieldBoundary();
            return null;
        }
        const discriminant = this.parseVariantDiscriminant();
        let fields = [];
        if (this.cur.peek().kind === "LBrace") {
            this.cur.next();
            fields = this.parseFieldList();
            this.expect("RBrace", "'}'");
        }
        else if (this.cur.peek().kind === "LParen") {
            this.cur.next();
            fields = this.parseTupleVariantFieldList();
            this.expect("RParen", "')'");
        }
        return withDiscriminant({
            name: nameTok.value,
            fields,
            span: spanBetween(nameTok, this.cur.peek()),
        }, discriminant);
    }
    parseVariantDiscriminant() {
        if (this.cur.peek().kind !== "Equals") {
            return undefined;
        }
        this.cur.next();
        const valueTok = this.expect("Number", "numeric discriminant");
        return valueTok?.value;
    }
    parseTupleVariantFieldList() {
        const fields = [];
        for (;;) {
            this.cur.eatNewlines();
            if (this.cur.peek().kind === "RParen" || this.cur.peek().kind === "EOF") {
                return fields;
            }
            const type = this.parseTypeRef();
            if (type === null) {
                return fields;
            }
            fields.push({
                name: `_${String(fields.length)}`,
                type,
                span: type.span,
            });
            this.cur.eatNewlines();
            if (this.cur.peek().kind === "Comma") {
                this.cur.next();
            }
            else {
                return fields;
            }
        }
    }
    parseTypeRef() {
        const nameTok = this.expect("Ident", "type name");
        if (nameTok === null) {
            return null;
        }
        let args = [];
        if (this.cur.peek().kind === "LAngle") {
            this.cur.next();
            args = this.parseCommaSeparated("RAngle", () => this.parseTypeRef());
            this.expect("RAngle", "'>'");
        }
        return {
            name: nameTok.value,
            args,
            span: spanBetween(nameTok, this.cur.peek()),
        };
    }
    expect(kind, what) {
        const t = this.cur.peek();
        if (t.kind === kind) {
            return this.cur.next();
        }
        this.diags.error(`expected ${what}, got ${describe(t)}`, t.line, t.col, t.length || 1);
        return null;
    }
    eatSeparator() {
        // Comma and/or newline are both valid separators between fields/variants.
        if (this.cur.peek().kind === "Comma") {
            this.cur.next();
        }
        this.cur.eatNewlines();
    }
    skipToFieldBoundary() {
        for (;;) {
            const t = this.cur.peek();
            if (t.kind === "Newline" || t.kind === "Comma" || t.kind === "RBrace" || t.kind === "EOF") {
                return;
            }
            this.cur.next();
        }
    }
    recoverToTopLevel() {
        let depth = 0;
        for (;;) {
            const t = this.cur.peek();
            if (t.kind === "EOF") {
                return;
            }
            if (t.kind === "LBrace") {
                depth++;
            }
            else if (t.kind === "RBrace") {
                if (depth === 0) {
                    this.cur.next();
                    return;
                }
                depth--;
            }
            else if (depth === 0 && (t.kind === "TypeKw" || t.kind === "UnionKw" || t.kind === "AliasKw")) {
                return;
            }
            this.cur.next();
        }
    }
}
function spanBetween(a, b) {
    return {
        line: a.line,
        col: a.col,
        offset: a.offset,
        length: Math.max(0, b.offset + b.length - a.offset),
    };
}
function describe(t) {
    if (t.kind === "EOF") {
        return "end of input";
    }
    if (t.kind === "Newline") {
        return "newline";
    }
    return `${t.kind} "${t.value}"`;
}
export function parsePartial(source) {
    const bag = new DiagnosticBag();
    const tokens = tokenize(source, bag);
    const parser = new Parser(new Cursor(tokens), bag);
    const ast = parser.parseDiagram();
    return { ast, diagnostics: bag.items };
}
/** Lift a parsed value + its diagnostics into a Result: `err` iff any diagnostic
 * has error severity. Shared by `parse` and `tokenizeResult`. */
function toResult(value, diagnostics) {
    return diagnostics.some((d) => d.severity === "error") ? err(diagnostics) : ok(value);
}
export function parse(source) {
    const { ast, diagnostics } = parsePartial(source);
    return toResult(ast, diagnostics);
}
export function tokenizePartial(source) {
    const bag = new DiagnosticBag();
    const tokens = tokenize(source, bag);
    return { tokens, diagnostics: bag.items };
}
export function tokenizeResult(source) {
    const { tokens, diagnostics } = tokenizePartial(source);
    return toResult(tokens, diagnostics);
}
//# sourceMappingURL=parser.js.map